import {AfterViewInit, Component, DoCheck, ElementRef, HostListener, Input, Output, EventEmitter, OnInit, ViewChild} from '@angular/core';

@Component({
  selector: 'app-exg-signature',
  templateUrl: './exg-signature.component.html',
  styles: [`
    :host {
      display: block;
      position: relative;
      cursor: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20height%3D%2224px%22%20viewBox%3D%220%20-960%20960%20960%22%20width%3D%2224px%22%20fill%3D%22%235f6368%22%3E%3Cpath%20d%3D%22M200-200h57l391-391-57-57-391%20391v57Zm-80%2080v-170l528-527q12-11%2026.5-17t30.5-6q16%200%2031%206t26%2018l55%2056q12%2011%2017.5%2026t5.5%2030q0%2016-5.5%2030.5T817-647L290-120H120Zm640-584-56-56%2056%2056Zm-141%2085-28-29%2057%2057-29-28Z%22%2F%3E%3C%2Fsvg%3E') 0 24 , auto;
    }
  `]
})
export class ExgSignatureComponent implements OnInit, AfterViewInit, DoCheck {

  constructor() {
    this._runOnce = true;
  }

  @ViewChild('canv') canv!: ElementRef<HTMLCanvasElement>;
  @Output() onStrokeEnd = new EventEmitter<MouseEvent | TouchEvent>();

  @Input() h: number | undefined;
  @Input() w: number | undefined;
  @Input() fg: string | undefined;
  @Input() bg: string | undefined;
  @Input() minWidth = 6; // 0.5
  @Input() maxWidth = 10.5; // 2.5

  htmlCanvas: HTMLCanvasElement | null = null;
  context: CanvasRenderingContext2D | null = null;
  points: Point[] = [];
  dotSize = (this.minWidth + this.maxWidth) / 2;
  mouseButtonDown = false;
  _velocityFilterWeight = 0.7;
  _lastVelocity = 0;
  _lastWidth: number = (this.minWidth + this.maxWidth) / 2;
  isEmpty = true;
  _runOnce: boolean;

  @HostListener('window:mouseup', ['$event'])
  onMouseUp(event: MouseEvent): void {
    this.handleMouseUp(event);
  }

  @HostListener('window:touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    this.handleTouchEnd(event);
  }

  ngDoCheck(): void {
    if (this._runOnce === true && this.htmlCanvas !== null) {
      if (this.w === undefined) {
        this.htmlCanvas.style.width = '100%';
        this.htmlCanvas.style.height = '100%';
      }
      const rect = this.htmlCanvas.getBoundingClientRect();
      this.htmlCanvas.width = this.w || rect.width;
      this.htmlCanvas.height = this.h || rect.height;
      this.clear();
      this._runOnce = false;
      this.maxWidth = 6.0;
    }
  }

  public clear(): void {
    if (!this.context || !this.htmlCanvas) return;
    this.context.fillStyle = this.bg || '#ffffff';
    this.context.clearRect(0, 0, this.htmlCanvas.width, this.htmlCanvas.height);
    this.context.fillRect(0, 0, this.htmlCanvas.width, this.htmlCanvas.height);
    this.context.fillStyle = this.fg || '#000000';
    this.reset();
  }

  public isPadEmpty(): boolean {
    return this.isEmpty;
  }

  public toDataURL(): string {
    return this.htmlCanvas?.toDataURL() || '';
  }

  public nativeElement(): HTMLCanvasElement | null {
    return this.htmlCanvas;
  }

  ngAfterViewInit(): void {
    this.htmlCanvas = this.canv.nativeElement;
    this.context = this.htmlCanvas.getContext('2d');
    this.htmlCanvas.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
  }

  handleMouseDown(event: MouseEvent): void {
    if (event.button === 0) {
      this.mouseButtonDown = true;
      this.strokeBegin(event);
    }
  }

  handleMouseMove(event: MouseEvent): void {
    if (this.mouseButtonDown) {
      this.strokeUpdate(event);
    }
  }

  handleMouseUp(event: MouseEvent): void {
    if (event.button === 0 && this.mouseButtonDown) {
      this.mouseButtonDown = false;
      this.strokeEnd();
      this.onStrokeEnd.emit(event);
    }
  }

  handleTouchStart(event: TouchEvent): void {
    const touch = event.changedTouches[0];
    this.strokeBegin(touch);
  }

  handleTouchMove(event: TouchEvent): void {
    event.preventDefault();
    const touch = event.changedTouches[0];
    this.strokeUpdate(touch);
  }

  handleTouchEnd(event: TouchEvent): void {
    const wasCanvasTouched = event.target === this.htmlCanvas;
    if (wasCanvasTouched) {
      this.mouseButtonDown = false;
      this.strokeEnd();
      this.onStrokeEnd.emit(event);
    }
  }

  strokeUpdate(event: MouseEvent | Touch): void {
    const point = this.createPoint(event);
    this.addPoint(point);
  }

  strokeBegin(event: MouseEvent | Touch): void {
    this.reset();
    this.strokeUpdate(event);
  }

  strokeDraw(point: Point): void {
    if (!this.context) {
      return;
    }
    this.context.beginPath();
    this.drawPoint(point.x, point.y, this.dotSize);
    this.context.closePath();
    this.context.fill();
  }

  strokeEnd(): void {
    const canDrawCurve = this.points.length > 2;
    const point = this.points[0];
    if (!canDrawCurve && point) {
      this.strokeDraw(point);
    }
  }

  reset(): void {
    this.points = [];
    this._lastVelocity = 0;
    this._lastWidth = (this.minWidth + this.maxWidth) / 2;
  }

  addPoint(point: Point): void {
    const points = this.points;
    points.push(point);
    if (points.length > 2) {
      if (points.length === 3) {
        points.unshift(points[0]);
      }
      let tmp = this.calculateCurveControlPoints(points[0], points[1], points[2]);
      const c2 = tmp.c2;
      tmp = this.calculateCurveControlPoints(points[1], points[2], points[3]);
      const c3 = tmp.c1;
      const curve = new Bezier(points[1], c2, c3, points[2]);
      this.addCurve(curve);
      points.shift();
    }
  }

  calculateCurveControlPoints(s1: Point, s2: Point, s3: Point): { c1: Point; c2: Point } {
    const dx1 = s1.x - s2.x, dy1 = s1.y - s2.y;
    const dx2 = s2.x - s3.x, dy2 = s2.y - s3.y;

    const m1 = {x: (s1.x + s2.x) / 2.0, y: (s1.y + s2.y) / 2.0};
    const m2 = {x: (s2.x + s3.x) / 2.0, y: (s2.y + s3.y) / 2.0};

    const l1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
    const l2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

    const dxm = (m1.x - m2.x);
    const dym = (m1.y - m2.y);

    const k = l2 / (l1 + l2);
    const cm = {x: m2.x + dxm * k, y: m2.y + dym * k};

    const tx = s2.x - cm.x;
    const ty = s2.y - cm.y;

    return {
      c1: new Point(m1.x + tx, m1.y + ty),
      c2: new Point(m2.x + tx, m2.y + ty)
    };
  }

  strokeWidth(velocity: number): number {
    return Math.max(this.maxWidth / (velocity + 1), this.minWidth);
  }

  addCurve(curve: Bezier): void {
    const startPoint = curve.startPoint;
    const endPoint = curve.endPoint;
    let velocity = endPoint.velocityFrom(startPoint);
    velocity = this._velocityFilterWeight * velocity + (1 - this._velocityFilterWeight) * this._lastVelocity;
    const newWidth = this.strokeWidth(velocity);
    this.drawCurve(curve, this._lastWidth, newWidth);
    this._lastVelocity = velocity;
    this._lastWidth = newWidth;
  }

  drawPoint(x: number, y: number, size: number): void {
    if (!this.context) {
      return;
    }
    this.context.moveTo(x, y);
    this.context.arc(x, y, size, 0, 2 * Math.PI, false);
    this.isEmpty = false;
  }

  drawCurve(curve: Bezier, startWidth: number, endWidth: number): void {
    if (!this.context) {
      return;
    }
    const widthDelta = endWidth - startWidth;
    const drawSteps = Math.floor(curve.length());
    this.context.beginPath();
    for (let i = 0; i < drawSteps; i++) {
      const t = i / drawSteps;
      const tt = t * t;
      const ttt = tt * t;
      const u = 1 - t;
      const uu = u * u;
      const uuu = uu * u;
      let x = uuu * curve.startPoint.x;
      x += 3 * uu * t * curve.control1.x;
      x += 3 * u * tt * curve.control2.x;
      x += ttt * curve.endPoint.x;
      let y = uuu * curve.startPoint.y;
      y += 3 * uu * t * curve.control1.y;
      y += 3 * u * tt * curve.control2.y;
      y += ttt * curve.endPoint.y;
      const width = startWidth + ttt * widthDelta;
      this.drawPoint(x, y, width);
    }
    this.context.closePath();
    this.context.fill();
  }

  createPoint(event: MouseEvent | Touch): Point {
    const rect = this.htmlCanvas!.getBoundingClientRect();
    return new Point(event.clientX - rect.left, event.clientY - rect.top);
  }

  ngOnInit(): void {
  }
}

class Point {
  x: number;
  y: number;
  time: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.time = Date.now();
  }

  public velocityFrom(start: Point): number {
    return (this.time !== start.time) ? this.distanceTo(start) / (this.time - start.time) : 1;
  }

  public distanceTo(start: Point): number {
    return Math.sqrt(Math.pow(this.x - start.x, 2) + Math.pow(this.y - start.y, 2));
  }
}

class Bezier {
  startPoint: Point;
  control1: Point;
  control2: Point;
  endPoint: Point;

  constructor(startPoint: Point, control1: Point, control2: Point, endPoint: Point) {
    this.startPoint = startPoint;
    this.control1 = control1;
    this.control2 = control2;
    this.endPoint = endPoint;
  }

  length(): number {
    const steps = 10;
    let length = 0;
    let px: number | undefined, py: number | undefined;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const cx = this.point(t, this.startPoint.x, this.control1.x, this.control2.x, this.endPoint.x);
      const cy = this.point(t, this.startPoint.y, this.control1.y, this.control2.y, this.endPoint.y);
      if (i > 0 && px !== undefined && py !== undefined) {
        const xdiff = cx - px;
        const ydiff = cy - py;
        length += Math.sqrt(xdiff * xdiff + ydiff * ydiff);
      }
      px = cx;
      py = cy;
    }
    return length;
  }

  point(t: number, start: number, c1: number, c2: number, end: number): number {
    return          start * (1.0 - t) * (1.0 - t)  * (1.0 - t)
      + 3.0 *  c1    * (1.0 - t) * (1.0 - t)  * t
      + 3.0 *  c2    * (1.0 - t) * t          * t
      +        end   * t         * t          * t;
  }
}
